// src/services/api.ts
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://or6d8rbho6.execute-api.us-east-1.amazonaws.com/prod';

// Export API_BASE_URL for use in other components
export { API_BASE_URL };

class ApiError extends Error {
  constructor(public status: number, message: string, public details?: any) {
    super(message);
    this.name = 'ApiError';
  }
}

// Utility to clean data before sending to API
// Converts empty objects, empty strings to null
function cleanRequestData(data: any): any {
  if (data === null || data === undefined) {
    return null;
  }

  if (typeof data !== 'object' || data instanceof Date) {
    return data;
  }

  if (Array.isArray(data)) {
    return data.map(cleanRequestData);
  }

  const cleaned: any = {};
  Object.keys(data).forEach(key => {
    const value = data[key];

    // Convert empty objects to null
    if (value && typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date) && Object.keys(value).length === 0) {
      cleaned[key] = null;
    }
    // Convert empty strings to null (except for specific fields that should keep empty strings)
    else if (value === '' && !['description', 'notes'].includes(key)) {
      cleaned[key] = null;
    }
    // Keep 0 as valid value
    else if (value === 0) {
      cleaned[key] = 0;
    }
    // Recursively clean nested objects
    else if (value && typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date)) {
      cleaned[key] = cleanRequestData(value);
    }
    else {
      cleaned[key] = value;
    }
  });

  return cleaned;
}

class ApiService {
  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
      });

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        let errorDetails = null;

        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
          errorDetails = errorData.details || errorData;
        } catch {
          // If JSON parsing fails, try text
          const errorText = await response.text();
          if (errorText) {
            errorMessage = errorText;
          }
        }

        console.error(`API Error ${response.status}:`, errorMessage, errorDetails);
        throw new ApiError(response.status, errorMessage, errorDetails);
      }

      return await response.json();
    } catch (error) {
      console.error(`API ${options.method || 'GET'} ${endpoint} failed:`, error);
      throw error;
    }
  }

  async get<T>(endpoint: string, token?: string): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      },
    });
  }

  async post<T>(endpoint: string, data: any, token?: string): Promise<T> {
    const cleanedData = cleanRequestData(data);
    console.log('POST request data (cleaned):', cleanedData);

    return this.request<T>(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(cleanedData),
    });
  }

  async put<T>(endpoint: string, data: any, token?: string): Promise<T> {
    const cleanedData = cleanRequestData(data);
    console.log('PUT request data (cleaned):', cleanedData);

    return this.request<T>(endpoint, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(cleanedData),
    });
  }

  async delete<T>(endpoint: string, token?: string): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      },
    });
  }
}

const apiService = new ApiService();
export default apiService;